/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useReducer, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'

import { greenPrimary, CustomTheme } from '@masfront/common-b2c'

import { Box, Typography, Link } from '@material-ui/core'
import InfoIcon from '@material-ui/icons/Info'
import WarningIcon from '@material-ui/icons/Warning'
import { Alert } from '@material-ui/lab'
import { makeStyles, createStyles } from '@material-ui/styles'

import BonusAvailableSelect from './BonusAvailableSelect'

import { useAddBonuses, useFetchBonusTypesAvailable } from '../hooks'

import { selectAgent } from '~/auth/store/auth.selectors'
import {
  formatBonusNameAndPrice,
  getTotalPrice,
  getSelectedBonus,
} from '~/bonus/components/BonusesAvailableModal.helpers'
import { getBonusTypeById } from '~/bonus/helpers/bonus.helpers'
import { BonusType } from '~/bonus/model/bonus.model'
import { useBrand } from '~/brand'
import { getSubscriptionFromId } from '~/customer/helpers'
import useCustomer from '~/customer/useCustomer'
import { ContentModal, LineItem, CustomCircularProgress, B2CModal } from '~/ui/components'
import { formatAmount } from '~/utils'

const useStyles = makeStyles((theme: CustomTheme) =>
  createStyles({
    formControl: {
      margin: theme.spacing(6, 0),
      width: theme.spacing(115),
      '& .MuiSelect-select:focus': {
        background: 'none',
      },
      '& .MuiFormLabel-root.Mui-focused': {
        color: greenPrimary,
      },
      '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: greenPrimary,
      },
    },

    selectOutlined: {
      display: 'flex',
      justifyContent: 'space-between',
      '& .MuiTypography-root': {
        paddingRight: theme.spacing(4),
      },
    },

    spinner: {
      margin: theme.spacing(10),
    },

    alert: {
      width: theme.spacing(115),
      margin: theme.spacing(3, 6, 3, 0),
    },

    bonusesList: {
      maxHeight: '300px',
      overflowY: 'scroll',
    },
  }),
)

enum StateId {
  // Main States
  MAIN_LOADED = 'MAIN_LOADED',
  MAIN_ERROR = 'MAIN_ERROR',
  MAIN_LOADING = 'MAIN_LOADING',

  // Substates (MAIN_LOADED)
  MAIN_LOADED_ADDING_BONUSES = 'MAIN_LOADED_ADDING_BONUSES',
  MAIN_LOADED_SUMMARY = 'MAIN_LOADED_SUMMARY',

  // Substates (MAIN_APPLYING_BONUSES)
  MAIN_APPLYING_BONUSES_REQUESTING = 'MAIN_APPLYING_BONUSES_REQUESTING',
  MAIN_APPLYING_BONUSES_ERR = 'MAIN_APPLYING_BONUSES_ERR', // connection Error
  MAIN_APPLYING_BONUSES_SUCCESS = 'MAIN_APPLYING_BONUSES_SUCCESS',
  MAIN_APPLYING_BONUSES_PARTIAL_SUCCESS = 'MAIN_APPLYING_BONUSES_PARTIAL_SUCCESS',
}

export interface BonusesAvailableModalState {
  stateId: StateId
  bonusesSelected: string[]
}

enum ActionType {
  FETCH_BONUS_SVC_SUCCESS = 'FETCH_BONUS_SVC_SUCCESS',
  FETCH_BONUS_SVC_FAILED = 'FETCH_BONUS_SVC_FAILED',
  ADD_BONUS_LINE = 'ADD_BONUS_LINE',
  SELECT_BONUS_LINE = 'SELECT_BONUS_LINE',
  NEXT_STEP = 'NEXT_STEP',
  REMOVE_BONUS_LINE = 'REMOVE_BONUS_LINE',
  BONUS_SVC_SUCCESS = 'BONUS_SVC_SUCCESS',
  BONUS_SVC_PARTIAL_SUCCESS = 'BONUS_SVC_PARTIAL_SUCCESS',
  BONUS_SVC_FAILED = 'BONUS_SVC_FAILED',
}
interface addBonusLine {
  type: ActionType.ADD_BONUS_LINE
}

interface selectBonusLine {
  type: ActionType.SELECT_BONUS_LINE
  payload: {
    numLine: number
    bonusId: string
  }
}

interface nextStep {
  type: ActionType.NEXT_STEP
}

interface removeBonusLine {
  type: ActionType.REMOVE_BONUS_LINE
  payload: {
    numLine: number
  }
}
interface bonusSvcSuccess {
  type: ActionType.BONUS_SVC_SUCCESS
}

interface bonusSvcPartialSuccess {
  type: ActionType.BONUS_SVC_PARTIAL_SUCCESS
}

interface bonusSvcFailed {
  type: ActionType.BONUS_SVC_FAILED
}

interface fetchBonusSvcSuccess {
  type: ActionType.FETCH_BONUS_SVC_SUCCESS
}

interface fetchBonusSvcFailed {
  type: ActionType.FETCH_BONUS_SVC_FAILED
}

type BonusesAvailableModalActions =
  | fetchBonusSvcSuccess
  | fetchBonusSvcFailed
  | addBonusLine
  | selectBonusLine
  | nextStep
  | removeBonusLine
  | bonusSvcSuccess
  | bonusSvcPartialSuccess
  | bonusSvcFailed

function reducer(state: BonusesAvailableModalState, action: BonusesAvailableModalActions) {
  switch (state.stateId) {
    case StateId.MAIN_LOADING: {
      switch (action.type) {
        case ActionType.FETCH_BONUS_SVC_SUCCESS:
          return {
            ...state,
            stateId: StateId.MAIN_LOADED_ADDING_BONUSES,
          }

        case ActionType.FETCH_BONUS_SVC_FAILED: {
          return {
            ...state,
            stateId: StateId.MAIN_ERROR,
          }
        }

        case ActionType.NEXT_STEP: {
          const noEmptyBonusesSelected = state.bonusesSelected.filter((ele) => ele)
          return {
            ...state,
            stateId: StateId.MAIN_LOADED_SUMMARY,
            bonusesSelected: noEmptyBonusesSelected,
          }
        }

        default:
          return state
      }
    }

    case StateId.MAIN_LOADED_ADDING_BONUSES: {
      switch (action.type) {
        case ActionType.ADD_BONUS_LINE:
          return {
            ...state,
            bonusesSelected: [...state.bonusesSelected, ''],
          }

        case ActionType.SELECT_BONUS_LINE: {
          const newBonusesSelected = [...state.bonusesSelected]
          newBonusesSelected[action.payload.numLine] = action.payload.bonusId
          return {
            ...state,
            bonusesSelected: [...newBonusesSelected],
          }
        }

        case ActionType.NEXT_STEP: {
          const noEmptyBonusesSelected = state.bonusesSelected.filter((ele) => ele)
          return {
            ...state,
            stateId: StateId.MAIN_LOADED_SUMMARY,
            bonusesSelected: noEmptyBonusesSelected,
          }
        }

        default:
          return state
      }
    }

    case StateId.MAIN_LOADED_SUMMARY: {
      switch (action.type) {
        case ActionType.REMOVE_BONUS_LINE: {
          const newBonusesSelected = state.bonusesSelected.filter(
            (ele, i: number) => i !== action.payload.numLine,
          )

          if (newBonusesSelected.length === 0) {
            return {
              ...state,
              bonusesSelected: [''],
              stateId: StateId.MAIN_LOADED_ADDING_BONUSES,
            }
          }

          return {
            ...state,
            bonusesSelected: newBonusesSelected,
          }
        }

        case ActionType.ADD_BONUS_LINE:
          return {
            ...state,
            stateId: StateId.MAIN_LOADED_ADDING_BONUSES,
          }

        case ActionType.NEXT_STEP:
          return {
            ...state,
            stateId: StateId.MAIN_APPLYING_BONUSES_REQUESTING,
          }

        default:
          return state
      }
    }

    case StateId.MAIN_APPLYING_BONUSES_REQUESTING: {
      switch (action.type) {
        case ActionType.BONUS_SVC_SUCCESS:
          return {
            ...state,
            stateId: StateId.MAIN_APPLYING_BONUSES_SUCCESS,
          }

        case ActionType.BONUS_SVC_PARTIAL_SUCCESS:
          return {
            ...state,
            stateId: StateId.MAIN_APPLYING_BONUSES_PARTIAL_SUCCESS,
          }

        case ActionType.BONUS_SVC_FAILED:
          return {
            ...state,
            stateId: StateId.MAIN_APPLYING_BONUSES_ERR,
          }

        default:
          return state
      }
    }

    default:
      return state
  }
}

export interface BonusesAvailableModalProps {
  subscriptionId: string
  isOpen: boolean
  onClose?: () => void
}

const BonusesAvailableModal: React.FC<BonusesAvailableModalProps> = ({
  subscriptionId,
  isOpen,
  onClose,
}) => {
  const classes = useStyles()
  const { customer } = useCustomer()
  const subscription = customer && getSubscriptionFromId(customer, subscriptionId)
  const msisdn = subscription?.identificationNr
  const { brand } = useBrand()
  const { agent } = useSelector(selectAgent)
  const fetchBonusTypesAvailableSvc = useFetchBonusTypesAvailable()
  const addBonusesSvc = useAddBonuses()

  const initialState = { bonusesSelected: [''], stateId: StateId.MAIN_LOADING }
  const [state, dispatch] = useReducer(reducer, initialState)
  const [componentLifeCycle] = useState({ unmounted: false })
  function onChangeBonus(selectId: number, bonusId: string) {
    dispatch({
      type: ActionType.SELECT_BONUS_LINE,
      payload: {
        numLine: selectId,
        bonusId,
      },
    })
  }

  function onAddBonus() {
    dispatch({
      type: ActionType.ADD_BONUS_LINE,
    })
  }

  function onRemoveBonus(numLine: number) {
    dispatch({
      type: ActionType.REMOVE_BONUS_LINE,
      payload: {
        numLine,
      },
    })
  }

  function onNextStep() {
    switch (state.stateId) {
      case StateId.MAIN_LOADED_ADDING_BONUSES:
        dispatch({
          type: ActionType.NEXT_STEP,
        })

        break

      case StateId.MAIN_LOADED_SUMMARY:
        {
          const bonusesIds = state.bonusesSelected.filter((id: string) => id)
          const bonuses = bonusesIds.map((bonusId: string) => {
            const bonus = getBonusTypeById(fetchBonusTypesAvailableSvc.bonuses, bonusId)
            return {
              id: bonus.id,
              name: bonus.name,
            }
          })
          const personal = {
            firstName: customer?.personal?.firstName || '',
            middleName: customer?.personal?.middleName || '',
            lastName: customer?.personal?.lastName || '',
            otherPhone: customer?.personal?.otherPhone || '',
            documentId: customer?.documentValue || '',
          }
          addBonusesSvc.addBonuses(
            { personal, msisdn: String(msisdn), bonuses },
            subscriptionId,
            brand,
            agent?.sub,
          )
          dispatch({
            type: ActionType.NEXT_STEP,
          })
        }

        break

      default:
    }
  }

  useEffect(
    () => () => {
      componentLifeCycle.unmounted = true
    },
    [],
  )

  useEffect(() => {
    switch (state.stateId) {
      case StateId.MAIN_LOADING:
        if (fetchBonusTypesAvailableSvc.isInitialState()) {
          const subscription = customer && getSubscriptionFromId(customer, subscriptionId)
          if (subscription) {
            const fetchBonusTypesAvailableRequest = {
              subscriptionId,
              tariffId: subscription.commercialProduct.id,
              provinceId: String(customer && customer.addressInfo.stateId),
              brand,
              sub: agent?.sub,
            }

            fetchBonusTypesAvailableSvc.fetchBonusesAvailable(
              fetchBonusTypesAvailableRequest,
              componentLifeCycle,
            )
          }
        } else if (!fetchBonusTypesAvailableSvc.state.loading) {
          if (fetchBonusTypesAvailableSvc.state.data) {
            dispatch({
              type: ActionType.FETCH_BONUS_SVC_SUCCESS,
            })
          } else {
            dispatch({
              type: ActionType.FETCH_BONUS_SVC_FAILED,
            })
          }
        }

        break

      default:
    }
  }, [fetchBonusTypesAvailableSvc.state])

  useEffect(() => {
    switch (state.stateId) {
      case StateId.MAIN_APPLYING_BONUSES_REQUESTING:
        if (!addBonusesSvc.state.loading) {
          if (addBonusesSvc.state.data) {
            const anyError = addBonusesSvc.state.data.items.filter(
              (data) => data.status === 'ERROR',
            )
            dispatch({
              type: anyError.length
                ? ActionType.BONUS_SVC_PARTIAL_SUCCESS
                : ActionType.BONUS_SVC_SUCCESS,
            })
          } else {
            dispatch({
              type: ActionType.BONUS_SVC_FAILED,
            })
          }
        }

        break

      default:
    }
  }, [addBonusesSvc.state])

  function calculateComboValues(bonusIdsSelected: string[], allBonuses: BonusType[]) {
    return bonusIdsSelected.map((bonusIdSelected, idx, allBonusIdsSelected) => ({
      bonusId: bonusIdSelected,
      bonuses: allBonuses.filter(
        (bonus) => !allBonusIdsSelected.includes(bonus.id) || bonus.id === bonusIdSelected,
      ),
    }))
  }

  function getConfirmButtonTxtFromState(state: BonusesAvailableModalState) {
    switch (state.stateId) {
      case StateId.MAIN_LOADED_SUMMARY:
        return 'Aplicar Bono'

      default:
        return 'Continuar'
    }
  }

  function getConfirmButtonDisabledFromState(state: BonusesAvailableModalState) {
    switch (state.stateId) {
      case StateId.MAIN_LOADED_ADDING_BONUSES:
      case StateId.MAIN_LOADED_SUMMARY:
        return !state.bonusesSelected.some((bonus) => Boolean(bonus))

      default:
        return true
    }
  }

  function MainModalContentRenderer() {
    switch (state.stateId) {
      case StateId.MAIN_LOADING: {
        return (
          <Box display="flex">
            <CustomCircularProgress size={50} />
          </Box>
        )
      }

      case StateId.MAIN_ERROR: {
        return (
          <Box width="300px" pb={2}>
            <Alert severity="error" icon={<WarningIcon fontSize="inherit" />}>
              Ha ocurrido un error.
            </Alert>
          </Box>
        )
      }

      case StateId.MAIN_LOADED_ADDING_BONUSES: {
        const noBonusesLiteral = fetchBonusTypesAvailableSvc.hasCompatibles
          ? 'Todos los bonos compatibles ya están aplicados'
          : 'No existen bonos compatibles para esta tarifa'
        return (
          <Box>
            {fetchBonusTypesAvailableSvc.bonuses.length ? (
              <Box mb={2} className={classes.bonusesList}>
                <Typography variant="h4">SELECCIONA EL BONO</Typography>
                {calculateComboValues(
                  state.bonusesSelected,
                  fetchBonusTypesAvailableSvc.bonuses,
                ).map((pairValueBonuses, i) => (
                  <BonusAvailableSelect
                    key={pairValueBonuses.bonusId}
                    bonuses={pairValueBonuses.bonuses}
                    onChangeBonus={(bonusId) => onChangeBonus(i, bonusId)}
                    bonusSelectedId={pairValueBonuses.bonusId}
                  />
                ))}
              </Box>
            ) : (
              <Box maxWidth="450px" pb={2} data-hook="empty-bonuses-alert">
                <Alert icon={<WarningIcon fontSize="inherit" />} severity="warning">
                  {noBonusesLiteral}
                </Alert>
              </Box>
            )}
            {state.bonusesSelected.length < fetchBonusTypesAvailableSvc.bonuses.length && (
              <Box mt={3}>
                <Link component="button" onClick={() => onAddBonus()}>
                  <Typography className="link" variant="body1">
                    Añadir otro Bono
                  </Typography>
                </Link>
              </Box>
            )}
          </Box>
        )
      }

      case StateId.MAIN_LOADED_SUMMARY: {
        return (
          <Box>
            {fetchBonusTypesAvailableSvc.bonuses && (
              <Box>
                <Typography variant="h4">Bonos seleccionados</Typography>
                {state.bonusesSelected.map((bonusSelectedId: string, i: number) => (
                  <Box key={bonusSelectedId} display="flex" alignItems="left">
                    <Alert
                      severity="warning"
                      className={classes.alert}
                      icon={<InfoIcon fontSize="inherit" />}
                    >
                      {formatBonusNameAndPrice(
                        getBonusTypeById(fetchBonusTypesAvailableSvc.bonuses, bonusSelectedId),
                      )}
                    </Alert>
                    <Link component="button" onClick={() => onRemoveBonus(i)}>
                      <Typography className="link" variant="body1">
                        Eliminar
                      </Typography>
                    </Link>
                  </Box>
                ))}
                {state.bonusesSelected.length < fetchBonusTypesAvailableSvc.bonuses.length && (
                  <Box mt={3}>
                    <Link component="button" onClick={() => onAddBonus()}>
                      <Typography className="link" variant="body1">
                        Añadir otro Bono
                      </Typography>
                    </Link>
                  </Box>
                )}
                <Box mt={3}>
                  <LineItem
                    primaryText="Precio resultante"
                    secondaryText={formatAmount(
                      getTotalPrice(
                        getSelectedBonus(
                          fetchBonusTypesAvailableSvc.bonuses,
                          state.bonusesSelected,
                        ),
                      ),
                      2,
                      '€/mes',
                    )}
                  />
                </Box>
              </Box>
            )}
          </Box>
        )
      }

      default:
        return <Box> Not possible!! ...</Box>
    }
  }

  // Rendering
  switch (state.stateId) {
    case StateId.MAIN_APPLYING_BONUSES_REQUESTING:
      return (
        <ContentModal isOpen={isOpen} onClose={onClose} withoutButtons>
          <Box mb={20} display="flex" justifyContent="center">
            <Box className={classes.spinner}>
              <CustomCircularProgress size={100} />
            </Box>
          </Box>
        </ContentModal>
      )
      break

    case StateId.MAIN_APPLYING_BONUSES_SUCCESS:
      return (
        <B2CModal
          isOpen={isOpen}
          type="success"
          title="¡Se han añadido los bonos con éxito!"
          description="Puede que tardes unos segundos en verlos activos en la Suscripción"
          onClose={onClose}
        >
          <Box mt={10} mb={20} width="100%" textAlign="center">
            <Typography variant="subtitle2">Bonos añadidos:</Typography>
            {addBonusesSvc.responsesByBonus.map((bonus) => (
              <Typography variant="body1" color="textSecondary">
                - {getBonusTypeById(fetchBonusTypesAvailableSvc.bonuses, bonus.id)?.name}
              </Typography>
            ))}
          </Box>
        </B2CModal>
      )
      break

    case StateId.MAIN_APPLYING_BONUSES_PARTIAL_SUCCESS:
      return (
        <B2CModal
          isOpen={isOpen}
          onClose={onClose}
          type="error"
          title="Algunos bonos no se han podido añadir"
          description="Ha habido un problema con los siguientes bonos:"
        >
          <Box mt={10} mb={20} width="100%" textAlign="center">
            <Typography variant="subtitle2">Bonos añadidos:</Typography>
            {addBonusesSvc.responsesByBonus
              .filter((bonus) => bonus.status === 'OK')
              .map((b) => (
                <Typography variant="body1" color="textSecondary">
                  - {getBonusTypeById(fetchBonusTypesAvailableSvc.bonuses, b.id)?.name}
                </Typography>
              ))}
            <Box my={10} />
            <Typography variant="subtitle2">Bonos no añadidos:</Typography>
            {addBonusesSvc.responsesByBonus
              .filter((bonus) => bonus.status === 'ERROR')
              .map((b) => (
                <Typography variant="body1" color="textSecondary">
                  - {getBonusTypeById(fetchBonusTypesAvailableSvc.bonuses, b.id)?.name}
                </Typography>
              ))}
          </Box>
        </B2CModal>
      )
      break

    case StateId.MAIN_APPLYING_BONUSES_ERR:
      return (
        <B2CModal
          isOpen={isOpen}
          type="error"
          title="Ha habido un problema y no se han podido añadir los bonos"
          onClose={onClose}
        >
          <Box m={15} />
        </B2CModal>
      )
      break

    default:
      return (
        <ContentModal
          isOpen={isOpen}
          onClose={onClose}
          title="Añadir Bono"
          onNextStep={() => onNextStep()}
          confirmButtonDisabled={getConfirmButtonDisabledFromState(state)}
          confirmButtonTxt={getConfirmButtonTxtFromState(state)}
        >
          {subscription && (
            <Box pt={6} data-hook="add-bonus-modal">
              <LineItem
                primaryText="Tarifa actual"
                secondaryText={subscription.commercialProduct.description}
              />
              <Box m={9} />
              {MainModalContentRenderer()}
            </Box>
          )}
        </ContentModal>
      )
      break
  }
}

export default BonusesAvailableModal
